import { Download, Ellipsis, FileText } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import type { CardTabsAttachmentProps } from "./CardTabsLink.types";
import { TruncatedText } from "../../components/TruncatedText/TruncatedText.component";
import { useToolsLinkQuery } from "../../queries";
import {
  CardResourceStack,
  ResourceMenuButton,
  TypographyResourceLabel
} from "../CardTabsLink/CardResourceStack.styles";
import { ResourceDescriptionModal } from "../CardTabsLink/ResourceDescriptionModal.component";

export function CardTabsAttachment({
  dimension,
  disabled,
  id: attachmentId,
  name,
  shortDescription,
  showDescription = false,
  ...props
}: CardTabsAttachmentProps) {
  const { id } = useParams();
  const { t } = useTranslation();
  const modalId = useId();
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const hasDescription = showDescription && Boolean(shortDescription?.trim());

  const { data: toolLink } = useToolsLinkQuery({
    enabled: !disabled,
    learningObjectId: id ?? "",
    toolId: attachmentId
  });

  const handleDownload = () => {
    if (!disabled && toolLink?.publicUrl) {
      const link = document.createElement("a");
      link.href = toolLink.publicUrl;
      link.download = "";
      link.target = "_blank";
      link.rel = "noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <>
      <CardResourceStack
        tabIndex={ 0 }
        aria-label={ `${t("download_file")} ${name}` }
        { ...props }
        onClick={ handleDownload }
        $disabled={ disabled }
      >
        <FileText
          size={ "1rem" }
        />
        <TruncatedText
          text={ name }
          webkitLineClamp={ 1 }
        >
          <TypographyResourceLabel>
            { name }
          </TypographyResourceLabel>
        </TruncatedText>

        { hasDescription && (
          <ResourceMenuButton
            type="button"
            aria-label={ t("resource_description", { name }) }
            aria-haspopup="dialog"
            aria-expanded={ descriptionOpen }
            aria-controls={ descriptionOpen ? modalId : undefined }
            onClick={ (event) => {
              event.stopPropagation();
              setDescriptionOpen(true);
            } }
          >
            <Ellipsis size={ "1rem" } aria-hidden="true" />
          </ResourceMenuButton>
        ) }
        <Download
          size={ "1rem" }
          style={ {
            flexShrink: 0,
            marginLeft: hasDescription ? undefined : "auto"
          } }
        />
      </CardResourceStack>
      { hasDescription && (
        <ResourceDescriptionModal
          id={ modalId }
          open={ descriptionOpen }
          icon={ <FileText size={ "1rem" } aria-hidden="true" /> }
          title={ name }
          description={ shortDescription }
          actionLabel={ t("download_action") }
          actionIcon={ <Download size={ "1rem" } aria-hidden="true" /> }
          actionDisabled={ disabled || !toolLink?.publicUrl }
          onAction={ () => {
            handleDownload();
            setDescriptionOpen(false);
          } }
          onClose={ () => setDescriptionOpen(false) }
        />
      ) }
    </>
  );
}

// function bytesToMegabytes(bytes: number) {
//   const mb = bytes / 1000000;
//   return `${mb.toFixed(2)} MB`;
// }
